import { useParams, useNavigate } from 'react-router-dom';
import { config } from "../../data/config";
import { getCookies } from "cookies-next";
import axios from "axios";
import { useEffect, useState } from "react";
import BreadCrump from "../tools/breadcrump";
import HeaderOfPage from "../tools/headerOfPage";
import Swal from 'sweetalert2';
import { useTranslation } from 'react-i18next';

const inputstyle = "py-2.5 px-3 block w-full border border-gray-300 rounded-lg text-sm focus:border-blue-500 focus:ring-blue-500 disabled:opacity-50 disabled:pointer-events-none bg-white";

const defaultName = { ku: '', fa: '', en: '', ar: '', tr: '' };

export default function EditFacility() {
    const { t } = useTranslation();
    const { Id } = useParams();
    const navigate = useNavigate();
    const cookie = getCookies();
    const isNew = Id === 'new' || Id === undefined;
    const invalidId = !isNew && (Id === 'undefined' || !Id);

    const [data, setData] = useState<{
        name: Record<string, string>;
        code: string;
        active: boolean;
        arranged: number;
    }>({
        name: { ...defaultName },
        code: '',
        active: true,
        arranged: 1
    });
    const [loading, setLoading] = useState(!isNew && !invalidId);
    const [languages, setLanguages] = useState<Array<{ code: string; label: string }>>([
        { code: 'ku', label: 'Kurdish' },
        { code: 'fa', label: 'فارسی' },
        { code: 'en', label: 'English' },
        { code: 'ar', label: 'العربية' },
        { code: 'tr', label: 'Türkçe' }
    ]);

    const breadcrump = [
        { name: t('Dashboard'), url: '/dashboard' },
        { name: t('Real Estate System'), url: '/dashboard/RealEstate' },
        { name: t('Feature Management'), url: '/dashboard/RealEstate/facilities' },
        { name: isNew ? t('Add Feature') : t('Edit Feature'), url: null },
    ];

    const loadLanguages = async () => {
        try {
            const res = await axios.get(`${config.url}/v1/language`);
            const list = res.data?.message ?? [];
            const mapped = (Array.isArray(list) ? list : [])
                .filter((l: any) => l.active)
                .sort((a: any, b: any) => (a.arranged ?? 0) - (b.arranged ?? 0))
                .map((l: any) => ({ code: l.code, label: l.label || l.code }));
            if (mapped.length > 0) setLanguages(mapped);
        } catch (_) {}
    };

    const load = async () => {
        if (isNew || invalidId) return;
        try {
            const res = await axios({
                method: 'get',
                url: `${config.url}/v1/dashboard/realestate/facilities/${Id}`,
                headers: { Authorization: 'Bearer ' + cookie['token'] }
            });
            const msg = res.data.message || res.data;
            const name = msg.name && typeof msg.name === 'object'
                ? { ...defaultName, ...msg.name }
                : { ...defaultName, en: String(msg.name || '') };
            setData({
                name,
                code: msg.code || '',
                active: msg.active !== false,
                arranged: msg.arranged != null ? Number(msg.arranged) : 1
            });
        } catch (_) {
            Swal.fire(t("Error"), t("Could not load feature."), "error");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadLanguages();
    }, []);

    useEffect(() => {
        load();
    }, [Id]);

    const setName = (lang: string, value: string) => {
        setData(prev => ({
            ...prev,
            name: { ...prev.name, [lang]: value }
        }));
    };

    // Translate plain text using Google Translate (same as QaComponent)
    const translateText = async (text: string, fromLang: string, toLang: string): Promise<string> => {
        if (!text || String(text).trim() === '') return '';
        try {
            const response = await fetch(
                `https://translate.googleapis.com/translate_a/single?client=gtx&sl=${fromLang}&tl=${toLang}&dt=t&q=${encodeURIComponent(text)}`
            );
            if (!response.ok) return '';
            const result = await response.json();
            const translated = result[0]?.map((s: any) => s?.[0] || '').join('').trim();
            return translated || text;
        } catch (_) {
            return '';
        }
    };

    const handleAutoTranslate = async () => {
        const langCodes = languages.map(l => l.code);
        const sourceLang = langCodes.find(lang => (data.name[lang] || '').trim());
        if (!sourceLang) {
            Swal.fire(t('No content to translate'), t('Fill in at least one language first.'), 'info');
            return;
        }
        const sourceText = (data.name[sourceLang] || '').trim();
        if (!sourceText) {
            Swal.fire(t('No content to translate'), t('Fill in at least one language first.'), 'info');
            return;
        }

        const result = await Swal.fire({
            title: t('Auto translate to all other languages'),
            text: t('This will translate the current content to all other languages. Continue?'),
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: t('Yes, translate'),
            cancelButtonText: t('Cancel')
        });
        if (!result.isConfirmed) return;

        const updatedName = { ...data.name };
        const otherLangs = langCodes.filter(l => l !== sourceLang);
        for (const toLang of otherLangs) {
            const translated = await translateText(sourceText, sourceLang, toLang);
            if (translated) updatedName[toLang] = translated;
            await new Promise(r => setTimeout(r, 150));
        }
        setData(prev => ({ ...prev, name: updatedName }));
        Swal.fire(t('Done'), t('Content has been translated to all other languages'), 'success');
    };

    const save = async () => {
        const token = cookie['token'] || cookie.token;
        if (!token) {
            Swal.fire(t("Error"), t("Please log in again."), "error");
            return;
        }
        const codeTrim = (data.code || '').trim();
        if (!codeTrim) {
            Swal.fire(t("Validation"), t("Code is required."), "warning");
            return;
        }
        const hasName = Object.values(data.name).some(v => (v || '').trim());
        if (!hasName) {
            Swal.fire(t("Validation"), t("Name is required in at least one language."), "warning");
            return;
        }

        const body = {
            name: data.name,
            code: codeTrim,
            active: data.active,
            arranged: Number(data.arranged) || 1
        };
        const headers = {
            'Content-Type': 'application/json',
            Authorization: 'Bearer ' + token
        };

        try {
            if (isNew) {
                await axios.post(config.url + '/v1/dashboard/realestate/facilities', body, { headers });
                Swal.fire(t("Saved"), t("Feature created."), "success");
            } else {
                await axios.put(config.url + '/v1/dashboard/realestate/facilities/' + Id, body, { headers });
                Swal.fire(t("Saved"), t("Feature updated."), "success");
            }
            navigate('/dashboard/RealEstate/facilities');
        } catch (err: any) {
            const status = err?.response?.status;
            const msg = err?.response?.data?.message ?? err?.message ?? t("Could not save.");
            const fullMsg = status === 401
                ? t("Please log in again.")
                : status === 400 || status === 422
                    ? (typeof msg === 'string' ? msg : JSON.stringify(msg))
                    : err?.response?.data?.error ?? msg;
            Swal.fire(t("Error"), fullMsg, "error");
        }
    };

    if (loading) {
        return (
            <div className="text-left bg-white p-3 m-4 directions h-[100vh] mb-5 ">
                <BreadCrump data={breadcrump} />
                <p className="text-gray-500">{t("Loading...")}</p>
            </div>
        );
    }

    return (
        <div className="text-left bg-white p-3 m-4 directions h-[100vh] mb-5 ">
            <BreadCrump data={breadcrump} />
            <HeaderOfPage name={isNew ? t("Add Feature") : t("Edit Feature")} />

            <div className="max-w-2xl space-y-4">
                <div>
                    <div className="flex items-center justify-between gap-2 mb-1">
                        <label className="block text-sm font-medium text-gray-700">{t("Name")} (per language)</label>
                        <button
                            type="button"
                            onClick={handleAutoTranslate}
                            title={t("Auto translate to all other languages")}
                            className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-indigo-600 bg-indigo-50 hover:bg-indigo-100 rounded-lg border border-indigo-200 transition-colors"
                        >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129" />
                            </svg>
                            {t("Auto translate")}
                        </button>
                    </div>
                    {languages.map(lang => (
                        <div key={lang.code} className="flex items-center gap-2 mb-2">
                            <span className="w-8 text-gray-500 text-sm">{lang.code}</span>
                            <input
                                type="text"
                                value={data.name[lang.code] ?? ''}
                                onChange={(e) => setName(lang.code, e.target.value)}
                                className={inputstyle}
                                placeholder={lang.label}
                            />
                        </div>
                    ))}
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t("Code")} *</label>
                    <input
                        type="text"
                        value={data.code}
                        onChange={(e) => setData(prev => ({ ...prev, code: e.target.value }))}
                        className={inputstyle}
                        placeholder={t("e.g. PARKING, ELEVATOR")}
                        disabled={!isNew}
                    />
                    {!isNew && <p className="text-xs text-gray-500 mt-1">{t("Code cannot be changed after creation.")}</p>}
                </div>

                <div>
                    <label className="flex items-center gap-2 cursor-pointer">
                        <input
                            type="checkbox"
                            checked={data.active}
                            onChange={(e) => setData(prev => ({ ...prev, active: e.target.checked }))}
                            className="rounded border-gray-300"
                        />
                        <span className="text-sm font-medium text-gray-700">{t("Active")}</span>
                    </label>
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t("Order")}</label>
                    <input
                        type="number"
                        min={0}
                        value={data.arranged}
                        onChange={(e) => setData(prev => ({ ...prev, arranged: parseInt(e.target.value, 10) || 0 }))}
                        className={inputstyle}
                    />
                </div>

                <div className="flex gap-2 pt-4">
                    <button
                        type="button"
                        onClick={save}
                        className="py-2.5 px-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
                    >
                        {t("Save")}
                    </button>
                    <button
                        type="button"
                        onClick={() => navigate('/dashboard/RealEstate/facilities')}
                        className="py-2.5 px-4 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 font-medium"
                    >
                        {t("Cancel")}
                    </button>
                </div>
            </div>
        </div>
    );
}
